import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import AxiosUtil from '../../Utils/AxiosUtil';
import type { ColumnsType } from 'antd/es/table';
import {
    Alert as ServaAlert,
    Button,
    Checkbox,
    Col,
    Input,
    Row,
    Skeleton,
    Space,
    Spin,
    Table,
    Text,
    Title,
} from '@servahealth/serva-ui-kit';
import {
    buildLNKErrorMessage,
    filterLinkedAdminRows,
    updateLinkedAdminRow,
} from '../Shared/LNKHelper';
import { invalidateReportMenu } from '../Shared/ReportMenuQuery';
import Administration from '../../Constants/Endpoints/Administration';
import AddDelTenantAdminUserRequest from '../../Interfaces/Requests/Administration/AddDelTenantAdminUserRequest';
import GetTenantAdminUserModelsUsingTenantIDRequest from '../../Interfaces/Requests/Administration/GetTenantAdminUserModelsUsingTenantIDRequest';
import GetTenantAdminUserModelsUsingTenantIDResponse from '../../Interfaces/Responses/Administration/GetTenantAdminUserModelsUsingTenantIDResponse';

type TenantUserProps = {
    tenantId: number;
    tenantName: string;
    close: () => void;
};

const TenantUsers: React.FC<TenantUserProps> = ({ ...props }) => {
    const queryClient = useQueryClient();

    const [users, setUsers] = useState<GetTenantAdminUserModelsUsingTenantIDResponse[]>([]);
    const [hideUnlinkedUsers, setHideUnlinkedUsers] = useState(false);
    const [searchText, setSearchText] = useState('');

    const [currentError, setCurrentError] = useState('');

    const { isLoading: isGetTenantUsersLoading, isError: isGetTenantUsersError, error: getTenantUsersError, refetch: refetchTenantUsers } = useQuery(
        ['tenant-admin-users', props.tenantId],
        async () => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.GET_TENANT_ADMIN_USER_MODELS_USING_TENANT_ID}`;
                const requestData: GetTenantAdminUserModelsUsingTenantIDRequest = {
                    gtu_tenantid: props.tenantId,
                };
                return await AxiosUtil.get<GetTenantAdminUserModelsUsingTenantIDResponse[], GetTenantAdminUserModelsUsingTenantIDRequest>(url, requestData);
            } catch (err) {
                setCurrentError('GET_TENANT_ADMIN_USER_MODELS_USING_TENANT_ID: [Get Tenant Users] Internal Error: ' + err);
                console.error('GET_TENANT_ADMIN_USER_MODELS_USING_TENANT_ID [Get Tenant Users] Internal Error: ', err);
                return Promise.reject(err);
            }
        },
        {
            enabled: props.tenantId > 0,
            onError: (err: unknown) => {
                setCurrentError('GET_TENANT_ADMIN_USER_MODELS_USING_TENANT_ID: [Get Tenant Users] Internal Error: ' + err);
                console.error('GET_TENANT_ADMIN_USER_MODELS_USING_TENANT_ID [Get Tenant Users] Internal Error: ', err);
            },
            onSuccess: (response: GetTenantAdminUserModelsUsingTenantIDResponse[]) => {
                if (response) {
                    setUsers(response);
                }
            },
        }
    );

    const { mutate: addRemoveUser, isLoading: isAddRemoveUserLoading, isError: isAddRemoveUserError, error: getAddRemoveUserError } = useMutation(
        async ({ add, user, }: {
            add: boolean;
            user: GetTenantAdminUserModelsUsingTenantIDResponse;
        }) => {
            let responseData = 0;
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.ADD_DEL_TENANT_ADMIN_USERS}`;
                const addDelTenantAdminUsers: AddDelTenantAdminUserRequest = {
                    id: user.id,
                    userName: user.userName,
                    tenantId: props.tenantId,
                    tenantName: props.tenantName,
                    isAdd: add,
                    isDel: !add,
                };
                const requestData: AddDelTenantAdminUserRequest[] = [ addDelTenantAdminUsers, ];
                responseData = await AxiosUtil.post<number, AddDelTenantAdminUserRequest[]>(url, requestData);
            } catch (err) {
                setCurrentError('ADD_DEL_TENANT_ADMIN_USERS: [Add/Remove Tenant Users] Internal Error: ' + err);
                console.error('ADD_DEL_TENANT_ADMIN_USERS [Add/Remove Tenant Users] Internal Error: ', err);
                return Promise.reject(err);
            }
            return { response: responseData, add, user };
        },
        {
            onError: (err: unknown) => {
                setCurrentError('ADD_DEL_TENANT_ADMIN_USERS: [Add/Remove Tenant Users] Internal Error: ' + err);
                console.error('ADD_DEL_TENANT_ADMIN_USERS [Add/Remove Tenant Users] Internal Error: ', err);
            },
            onSuccess: ({ response, add, user }) => {
                if (response) {
                    setUsers(updateLinkedAdminRow(users, user.id, add));
                    void refetchTenantUsers();
                    invalidateReportMenu(queryClient);
                }
            },
        }
    );

    const handleCheckboxChange = (checked: boolean, user: GetTenantAdminUserModelsUsingTenantIDResponse) => {
        const params = { add: checked, user: user, };
        addRemoveUser(params);
    };

    const columns: ColumnsType<GetTenantAdminUserModelsUsingTenantIDResponse> = [
        {
            title: 'Linked',
            key: 'isLinked',
            render: (text: unknown, record: GetTenantAdminUserModelsUsingTenantIDResponse) => (
                <>
                    <Checkbox
                        checked={record.isLinked}
                        onChange={(e) => handleCheckboxChange(e.target.checked, record)}
                    ></Checkbox>
                </>
            ),
        },
        { title: 'User', dataIndex: 'userName', key: 'userName' },
    ];

    const visibleUsers = filterLinkedAdminRows(
        users,
        hideUnlinkedUsers,
        searchText.trim(),
        (user, normalizedSearchText) => normalizedSearchText === ''
            || user.firstName.toLowerCase().includes(normalizedSearchText)
            || user.lastName.toLowerCase().includes(normalizedSearchText)
            || user.email.toLowerCase().includes(normalizedSearchText)
    );

    const handleShowEnabledUsers = () => {
        setHideUnlinkedUsers(!hideUnlinkedUsers);
    };

    const handleSearchTextChange = (aValue: React.ChangeEvent<HTMLInputElement>) => {
        setSearchText(aValue.target.value);
    };

    const doSearch = (sValue: string) => {
        setSearchText(sValue);
    };

    const determineWhichError = () => {
        return buildLNKErrorMessage(
            currentError,
            isGetTenantUsersError,
            getTenantUsersError,
            isAddRemoveUserError,
            getAddRemoveUserError
        );
    };

    return (
        <>
            <Spin spinning={isGetTenantUsersLoading || isAddRemoveUserLoading}>
                <div style={{ display: 'table', margin: 'auto', textAlign: 'center', width: '50%' }}>
                {
                    (
                        isGetTenantUsersError ||
                        isAddRemoveUserError
                    )
                    &&
                    (
                        <ServaAlert
                            className={'mb-2'}
                            message={determineWhichError()}
                            showIcon={true}
                            type='error'
                        ></ServaAlert>
                    )
                }
                </div>
                <Space direction='horizontal' style={{justifyContent: 'center', padding: '8px', width: '100%'}}>
                    <Text style={{height: '100%', width: '100%'}}>
                        <Title style={{backgroundColor: 'rgb(00, 55, 90)', borderRadius: '10px', color: 'white', height: '100%', padding: '8px', width: '100%'}} level={4}>Manage Tenant Links To Users</Title>
                    </Text>
                </Space>
                <div style={{ width: '100%', overflowX: 'auto' }}>
                    <Row gutter={16}>
                        <Col span={4}>
                            <Button
                                onClick={handleShowEnabledUsers}
                                size='small'
                                style={{
                                    background: 'none',
                                    backgroundColor: 'lightgray',
                                    color: 'blue',
                                    cursor: 'pointer',
                                }}
                                type='text'
                            >
                                {hideUnlinkedUsers ? 'Show All Users' : 'Hide Unlinked Users'}
                            </Button>
                        </Col>
                        <Col span={4} />
                        <Col span={8}>
                            <Input
                                value={searchText}
                                allowClear={true}
                                search={true}
                                style={{alignContent: 'center', width: '75%'}}
                                onChange={handleSearchTextChange}
                                onSearch={doSearch}
                            ></Input>
                        </Col>
                        <Col span={4} />
                        <Col span={4}><Text type='secondary' italic ellipsis>{props.tenantName}</Text></Col>
                    </Row>
                    <div style={{ padding: '4px' }} />
                    {isGetTenantUsersLoading && users.length === 0 ? (
                        <Skeleton />
                    ) : (
                        <Table rowKey='id' columns={columns} dataSource={visibleUsers} />
                    )}
                </div>
            </Spin>
        </>
    );
};

export default TenantUsers;
